import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldAlert, Smartphone, Lock, Loader2, ArrowLeft } from "lucide-react";

export default function SuperAdminLogin() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!phone.trim() || !password) {
      toast.error("Captura teléfono y contraseña");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/superadmin/login", {
        phone: phone.trim(),
        password,
      });
      // Persist token in same storage key used by /lib/api.js interceptor
      localStorage.setItem("unatesis_session_token", data.session_token);
      await refresh?.();
      toast.success("Acceso concedido");
      navigate("/superadmin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-paper flex items-center justify-center px-4" data-testid="superadmin-login-page">
      <div aria-hidden="true" className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[radial-gradient(closest-side,rgba(193,148,86,0.18),transparent_70%)] blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-[radial-gradient(closest-side,rgba(50,98,68,0.22),transparent_70%)] blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-paper/60 hover:text-paper mb-6">
          <ArrowLeft size={13} /> Volver al sitio
        </Link>

        <div className="rounded-3xl bg-[#1a2520] border border-paper/10 p-8 sm:p-10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-full bg-copper/20 text-copper">
              <ShieldAlert size={18} />
            </span>
            <div>
              <p className="text-[10px] tracking-[0.28em] uppercase text-copper font-medium">Acceso restringido</p>
              <h1 className="font-serif text-2xl text-paper leading-tight">Panel Super Admin</h1>
            </div>
          </div>
          <p className="mt-3 text-[12.5px] text-paper/65 leading-relaxed">
            Área exclusiva de operaciones. Acceso por número de celular registrado.
          </p>

          <form onSubmit={onSubmit} className="mt-7 flex flex-col gap-4" data-testid="superadmin-login-form">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10.5px] tracking-[0.2em] uppercase text-paper/60 font-medium">Celular</span>
              <div className="relative">
                <Smartphone size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-paper/40" />
                <input
                  data-testid="superadmin-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="3342802102"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-paper/5 border border-paper/15 text-paper placeholder:text-paper/30 outline-none focus:border-copper focus:ring-2 focus:ring-copper/20"
                />
              </div>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-[10.5px] tracking-[0.2em] uppercase text-paper/60 font-medium">Contraseña</span>
              <div className="relative">
                <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-paper/40" />
                <input
                  data-testid="superadmin-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••"
                  className="w-full pl-10 pr-4 py-3 rounded-lg bg-paper/5 border border-paper/15 text-paper placeholder:text-paper/30 outline-none focus:border-copper focus:ring-2 focus:ring-copper/20"
                />
              </div>
            </label>

            <button
              data-testid="superadmin-submit"
              type="submit"
              disabled={submitting}
              className="mt-2 inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-[13px] font-medium text-ink bg-copper hover:bg-copper/90 disabled:opacity-60 transition-all shadow-[0_18px_36px_-12px_rgba(193,148,86,0.5)]"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
              {submitting ? "Verificando…" : "Ingresar al panel"}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-[11px] text-paper/40">
          unaTESIS · Operaciones internas · Acceso registrado.
        </p>
      </div>
    </div>
  );
}
